import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "sonner";
import { CheckCircle2, Download, Eye, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getErrorMessage, isAuthExpiryError } from "@/utils/errorHandler";
import { acknowledgeCompanyDocument, fetchCompanyDocuments } from "@/services/companyDocumentService";
import CompanyDocumentAcknowledgeDialog from "@/components/company-documents/CompanyDocumentAcknowledgeDialog";
import {
  CompanyDocumentActiveCategory,
  CompanyDocumentCategoryFilter,
  CompanyDocumentCategoryTabs,
  CompanyDocumentPreviewDialog,
  CompanyDocumentsError,
  CompanyDocumentsGrid,
  CompanyDocumentsSkeleton,
  CompanyDocumentsToolbar,
  SegmentedControl,
} from "@/components/company-documents/CompanyDocumentsList";
import {
  canPreview,
  downloadCompanyDocument,
  includesText,
  needsAcknowledgement,
  openCompanyDocument,
} from "@/components/company-documents/companyDocumentUtils";

const ACKNOWLEDGE_TAB = "acknowledge"; // ?tab= value notifications link to

// Read-only, apart from acknowledging. There are deliberately no
// upload/edit/archive controls here — and the API refuses those calls for
// employees regardless.
const EmployeeCompanyDocuments = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const show = searchParams.get("tab") === ACKNOWLEDGE_TAB ? ACKNOWLEDGE_TAB : "all";
  const [acknowledgingId, setAcknowledgingId] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [category, setCategory] = useState("all");
  const [preview, setPreview] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const loadDocuments = useCallback(async () => {
    setLoadError(null);
    try {
      setDocuments(await fetchCompanyDocuments());
    } catch (error) {
      if (!isAuthExpiryError(error)) {
        setLoadError(getErrorMessage(error, "Failed to load company documents"));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const outstandingCount = useMemo(() => documents.filter(needsAcknowledgement).length, [documents]);
  const asksForAcknowledgement = useMemo(
    () => documents.some((doc) => doc.acknowledgement_required && doc.my_acknowledgement),
    [documents],
  );
  // From the list, so opening the document or a refresh shows up in the dialog.
  const acknowledgingDoc = useMemo(
    () => documents.find((doc) => doc._id === acknowledgingId && needsAcknowledgement(doc)) || null,
    [documents, acknowledgingId],
  );

  // Search narrows first so the category tab counts match what is shown.
  const searchedDocs = useMemo(
    () =>
      documents.filter(
        (doc) => includesText(doc, searchTerm) && (show !== ACKNOWLEDGE_TAB || needsAcknowledgement(doc)),
      ),
    [documents, searchTerm, show],
  );
  const visibleDocs = useMemo(
    () => (category === "all" ? searchedDocs : searchedDocs.filter((d) => d.category === category)),
    [searchedDocs, category],
  );

  const reportError = (error, fallback) => {
    if (isAuthExpiryError(error)) return;
    // The document may have been archived since the list loaded.
    if (error?.response?.status === 404) {
      toast.error("This document is no longer available");
      loadDocuments();
      return;
    }
    toast.error(getErrorMessage(error, fallback));
  };

  // The link request is what the server records as opening it.
  const markOpened = (doc) => {
    if (doc.my_acknowledgement?.status !== "not_opened") return;
    setDocuments((current) =>
      current.map((d) =>
        d._id === doc._id
          ? { ...d, my_acknowledgement: { ...d.my_acknowledgement, status: "opened" } }
          : d,
      ),
    );
  };

  const handleView = async (doc) => {
    try {
      const url = await openCompanyDocument(doc);
      markOpened(doc);
      if (url) setPreview({ doc, url });
    } catch (error) {
      reportError(error, "Failed to open document");
    }
  };

  const handleDownload = async (doc) => {
    setBusyId(doc._id);
    try {
      const fileName = await downloadCompanyDocument(doc);
      markOpened(doc);
      toast.success(`Downloading ${fileName}`);
    } catch (error) {
      reportError(error, "Download failed");
    } finally {
      setBusyId(null);
    }
  };

  // Clicking a title views what the browser can show and downloads the rest.
  const handleOpen = (doc) => (canPreview(doc) ? handleView(doc) : handleDownload(doc));

  const handleAcknowledge = async (doc) => {
    try {
      await acknowledgeCompanyDocument(doc._id);
      toast.success(`"${doc.title}" acknowledged`);
      setAcknowledgingId(null);
      await loadDocuments();
    } catch (error) {
      reportError(error, "Failed to acknowledge document");
      // The file was replaced or the requirement removed since this list loaded.
      if (error?.response?.status === 409) await loadDocuments();
      throw error;
    }
  };

  const renderStatus = (doc) => {
    if (!doc.acknowledgement_required || !doc.my_acknowledgement) return null;
    if (!needsAcknowledgement(doc)) {
      return (
        <p className="mt-3 flex items-center gap-1.5 text-xs font-medium text-emerald-700 dark:text-emerald-400">
          <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
          Acknowledged on {format(new Date(doc.my_acknowledgement.acknowledged_at), "dd MMM yyyy")}
        </p>
      );
    }
    return (
      <Button size="sm" className="mt-3 w-full" onClick={() => setAcknowledgingId(doc._id)}>
        Acknowledge
      </Button>
    );
  };

  const renderActions = (doc) => (
    <>
      {canPreview(doc) && (
        <Button
          variant="ghost"
          size="icon"
          onClick={() => handleView(doc)}
          aria-label={`View ${doc.title}`}
          title="View"
          className="h-9 w-9 text-muted-foreground hover:bg-primary/10 hover:text-primary sm:h-8 sm:w-8"
        >
          <Eye className="h-4 w-4" />
        </Button>
      )}
      <Button
        variant="ghost"
        size="icon"
        onClick={() => handleDownload(doc)}
        disabled={busyId === doc._id}
        aria-label={`Download ${doc.title}`}
        title="Download"
        className="h-9 w-9 text-muted-foreground hover:bg-primary/10 hover:text-primary sm:h-8 sm:w-8"
      >
        {busyId === doc._id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
      </Button>
    </>
  );

  if (loading) return <CompanyDocumentsSkeleton />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 sm:text-2xl">Company Documents</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 sm:text-base">
          Easily access and download documents shared by your company
        </p>
      </div>

      {loadError ? (
        <CompanyDocumentsError message={loadError} onRetry={loadDocuments} />
      ) : (
        <>
          <CompanyDocumentCategoryTabs documents={searchedDocs} value={category} onChange={setCategory} />
          <CompanyDocumentsToolbar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            filter={<CompanyDocumentCategoryFilter documents={searchedDocs} value={category} onChange={setCategory} />}
          >
            {asksForAcknowledgement && (
              <SegmentedControl
                ariaLabel="Documents to show"
                value={show}
                onChange={(next) => {
                  setSearchParams(next === ACKNOWLEDGE_TAB ? { tab: ACKNOWLEDGE_TAB } : {}, { replace: true });
                  setCategory("all");
                }}
                options={[
                  { value: "all", label: "All documents" },
                  { value: ACKNOWLEDGE_TAB, label: `To acknowledge (${outstandingCount})` },
                ]}
              />
            )}
          </CompanyDocumentsToolbar>
          <CompanyDocumentActiveCategory
            value={category}
            count={visibleDocs.length}
            onClear={() => setCategory("all")}
          />
          <CompanyDocumentsGrid
            documents={visibleDocs}
            onOpen={handleOpen}
            renderActions={renderActions}
            renderStatus={renderStatus}
            isFiltered={Boolean(searchTerm.trim()) || category !== "all"}
            emptyTitle={show === ACKNOWLEDGE_TAB ? "Nothing to acknowledge" : "No company documents yet"}
            emptyDescription={
              show === ACKNOWLEDGE_TAB
                ? "You have acknowledged everything your company has asked you to."
                : "Documents shared by your company will appear here."
            }
          />
        </>
      )}

      <CompanyDocumentPreviewDialog preview={preview} onClose={() => setPreview(null)} />

      <CompanyDocumentAcknowledgeDialog
        doc={acknowledgingDoc}
        onClose={() => setAcknowledgingId(null)}
        onOpenDocument={handleOpen}
        onConfirm={handleAcknowledge}
      />
    </div>
  );
};

export default EmployeeCompanyDocuments;
